import type { UnknownAction } from "@reduxjs/toolkit";
import { useAppDispatch } from "../../../../services/redux/hooks/use-dispatch";
import {
  createStorageObjectVersionAction,
  deleteStorageObjectVersionAction,
  updateStorageObjectVersionAction,
} from "../actions/versioning-actions";
import { setObjectVersions, setSelectedVersionId } from "../slice";
import type { StorageServiceVersions } from "../types";
import { removeVersionAndRefresh } from "../utils/removeVersionAndRefresh";
import { useStorageSelectors } from "./use-storage-selectors";
import {
  StorageObjectValue,
  StorageScope,
} from "../../../../services/api/instances/storageApi/types/service_types";

type SetValueAction<T> = (payload: T) => UnknownAction;

export const useStorageObjectVersions = (service: StorageScope) => {
  const dispatch = useAppDispatch();
  const { versions, selectedVersion, selectedId, selectedObject } =
    useStorageSelectors(service);

  const createObjectVersion = (name: string) => {
    dispatch(createStorageObjectVersionAction({ service, name }));
  };

  const updateObjectVersion = (name: string) => {
    dispatch(updateStorageObjectVersionAction({ service, name }));
  };

  const restoreObjectVersion = <T extends StorageObjectValue>(
    id: string,
    setValueAction: SetValueAction<T>,
  ) => {
    const value = versions?.[selectedId as string].find(
      (version) => version.version_id === id,
    )?.value;
    dispatch(
      setSelectedVersionId({
        service,
        version_id: id,
      }),
    );
    dispatch(setValueAction(value as T));
  };

  const refreshVersion = (id: string) => {
    const { newVersions, objectVersions } = removeVersionAndRefresh(
      versions as StorageServiceVersions,
      selectedId as string,
      id,
    );
    dispatch(setObjectVersions({ service, versions: newVersions }));
    dispatch(
      deleteStorageObjectVersionAction({
        service,
        versionId: id,
      }),
    );
    dispatch(
      createStorageObjectVersionAction({
        service,
        name: selectedVersion?.version_name as string,
      }),
    );
  };

  const deleteObjectVersion = () => {
    const { newVersions, objectVersions } = removeVersionAndRefresh(
      versions as StorageServiceVersions,
      selectedId as string,
      selectedVersion?.version_id as string,
    );
    dispatch(
      deleteStorageObjectVersionAction({
        service,
        versionId: selectedVersion?.version_id as string,
      }),
    );
    if (objectVersions.length !== 0) {
      const nextObjectVersion = objectVersions.at(-1);
      dispatch(
        setSelectedVersionId({
          service,
          version_id: nextObjectVersion?.version_id as string,
        }),
      );
    }
    dispatch(setObjectVersions({ service, versions: newVersions }));
  };

  return {
    versions,
    selectedVersion,
    createObjectVersion,
    updateObjectVersion,
    restoreObjectVersion,
    refreshVersion,
    deleteObjectVersion,
  };
};
